/**
 * Mensajes de una conversación, en vivo desde Chatwoot. Nunca se guardan.
 *
 * Spec 024 · FR-010: el recuadro sigue al último si se está al final.
 * Spec 024 · FR-011: el bocadillo nuevo entra; los que ya estaban, no.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Button } from './ui/button'
import { cn } from '../lib/cn'
import { tramoNuevo, type EstadoBurbujas } from '../lib/burbujas-vivas'
import { hora } from '../lib/tiempo'
import type { MensajeVista, SobreMensajes } from '../lib/tipos'

function menosMovimiento(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function MensajesConversacion({
    sobre,
    onReintentar,
    recargando,
    compacto = false,
    resaltar = null,
    resaltarClave = 0,
    idHilo = null,
    encajar = false,
    className,
}: {
    sobre: SobreMensajes
    onReintentar: () => void
    recargando: boolean
    compacto?: boolean
    /** Índice del mensaje a sombrear y traer a la vista (024 · FR-006). */
    resaltar?: number | null
    resaltarClave?: number
    /** Cambia al saltar de conversación: el tramo nuevo se cuenta de cero. */
    idHilo?: string | number | null
    /** Llena el hueco del diálogo: el recuadro es el único scroll (024 · FR-010). */
    encajar?: boolean
    className?: string
}) {
    const caja = useRef<HTMLDivElement>(null)
    const marcas = useRef<Array<HTMLDivElement | null>>([])
    const tramo = useRef<EstadoBurbujas>({ hilo: null, visto: null })
    const primera = useRef(true)
    const empujando = useRef(false)
    const [pegado, setPegado] = useState(true)

    const msgs = sobre.status === 'ok' ? (sobre.data ?? []) : []
    const r = tramoNuevo(idHilo ?? null, msgs.length, tramo.current)
    tramo.current = r.estado
    const animarDesde = r.animarDesde

    useEffect(() => {
        primera.current = true
        setPegado(true)
    }, [idHilo])

    useEffect(() => {
        if (resaltar === null) return
        const cajaEl = caja.current
        const el = marcas.current[resaltar]
        if (!cajaEl || !el) return
        // Solo el recuadro. `scrollIntoView` también mueve la ventana.
        const c = cajaEl.getBoundingClientRect()
        const e = el.getBoundingClientRect()
        cajaEl.scrollTo({
            top: cajaEl.scrollTop + (e.top - c.top) - c.height / 2 + e.height / 2,
            behavior: menosMovimiento() ? 'auto' : 'smooth',
        })
    }, [resaltar, resaltarClave])

    useLayoutEffect(() => {
        const el = caja.current
        if (!el) return
        // `overflow-y: auto` en Chrome captura la rueda aunque no haya
        // qué scrollar. Si el hilo cabe, que la rueda mueva la página.
        if (encajar) {
            el.style.overflowY = 'auto'
            return
        }
        el.style.overflowY = el.scrollHeight > el.clientHeight + 1 ? 'auto' : 'visible'
    }, [msgs.length, encajar, compacto, className])

    useLayoutEffect(() => {
        if (resaltar !== null) return
        if (!pegado) return
        const el = caja.current
        if (!el) return
        const instantaneo = primera.current || menosMovimiento()
        primera.current = false
        empujando.current = true
        el.scrollTo({ top: el.scrollHeight, behavior: instantaneo ? 'auto' : 'smooth' })
        const t = window.setTimeout(() => {
            empujando.current = false
        }, instantaneo ? 0 : 520)
        return () => window.clearTimeout(t)
    }, [msgs.length, pegado, resaltar, idHilo])

    if (sobre.status === 'unavailable') {
        return (
            <p className="text-sm text-neutral-600">
                Sin cuenta de Chatwoot resuelta: no hay mensajes que mostrar.
            </p>
        )
    }
    if (sobre.status === 'error') {
        return (
            <div className="rounded border border-neutral-800 p-4 text-sm">
                <p className="text-amber-400">No se pudieron cargar los mensajes (Chatwoot: {sobre.motivo}).</p>
                <p className="mt-1 text-xs text-neutral-600">
                    El resto del detalle es correcto: el fallo de Chatwoot no lo afecta.
                </p>
                <Button variant="outline" size="sm" className="mt-2" onClick={onReintentar} disabled={recargando}>
                    {recargando ? 'Reintentando…' : 'Reintentar'}
                </Button>
            </div>
        )
    }
    if (msgs.length === 0) return <p className="text-sm text-neutral-600">Sin mensajes.</p>
    return (
        <div
            ref={caja}
            className={cn(
                'overflow-y-auto rounded border border-neutral-800',
                encajar
                    ? 'overscroll-contain max-h-[calc(90vh-11rem)] space-y-2 p-3'
                    : compacto
                      ? 'max-h-[42vh] space-y-1 p-2'
                      : 'max-h-[70vh] space-y-2 p-3',
                className,
            )}
            onScroll={(e) => {
                if (empujando.current) return
                const el = e.currentTarget
                setPegado(el.scrollHeight - el.scrollTop - el.clientHeight < 48)
            }}
        >
            {msgs.map((m, i) => (
                <HitoHilo
                    key={i}
                    m={m}
                    compacto={compacto}
                    resaltada={resaltar === i}
                    entra={i >= animarDesde}
                    atraso={Math.min((i - animarDesde) * 45, 180)}
                    refEl={(el) => {
                        marcas.current[i] = el
                    }}
                />
            ))}
        </div>
    )
}

function HitoHilo({
    m,
    compacto,
    resaltada,
    entra,
    atraso,
    refEl,
}: {
    m: MensajeVista
    compacto?: boolean
    resaltada: boolean
    entra: boolean
    atraso: number
    refEl: (el: HTMLDivElement | null) => void
}) {
    const papel = m.papel ?? (m.direction === 'other' ? 'actividad' : 'mensaje')
    if (papel === 'actividad') {
        return (
            <div
                ref={refEl}
                data-testid={resaltada ? 'mensaje-resaltado' : 'aviso-canal'}
                className="flex justify-center px-2 py-1"
            >
                <p className="max-w-[92%] text-center text-[11px] leading-snug text-neutral-500">
                    {m.content}
                    {m.at && (
                        <span className="ml-1.5 font-mono text-[10px] text-neutral-600">{hora(m.at)}</span>
                    )}
                </p>
            </div>
        )
    }
    if (papel === 'nota') {
        return (
            <div
                ref={refEl}
                data-testid={resaltada ? 'mensaje-resaltado' : 'nota-equipo'}
                className="flex justify-center px-1 py-1"
            >
                <div className="max-w-[92%] rounded-md border border-amber-900/50 bg-amber-950/30 px-2.5 py-1.5">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-amber-400/80">
                        Nota del equipo
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-amber-100/90">{m.content}</p>
                    {m.at && (
                        <span className="mt-0.5 block font-mono text-[10px] text-amber-700">{hora(m.at)}</span>
                    )}
                </div>
            </div>
        )
    }
    return (
        <Burbuja m={m} compacto={compacto ?? false} resaltada={resaltada} entra={entra} atraso={atraso} refEl={refEl} />
    )
}

function AdjuntosDelMensaje({ adjuntos }: { adjuntos?: MensajeVista['adjuntos'] }) {
    if (!adjuntos || adjuntos.length === 0) return null
    return (
        <div className="mb-1 space-y-1">
            {adjuntos.map((a) =>
                a.tipo === 'audio' ? (
                    <audio key={a.url} controls preload="none" src={a.url} className="max-w-full" />
                ) : (
                    <img key={a.url} src={a.url} alt="Imagen enviada" className="max-h-48 max-w-full rounded" />
                ),
            )}
        </div>
    )
}

function Burbuja({
    m,
    compacto,
    resaltada,
    entra,
    atraso,
    refEl,
}: {
    m: MensajeVista
    compacto?: boolean
    resaltada: boolean
    entra: boolean
    atraso: number
    refEl: (el: HTMLDivElement | null) => void
}) {
    const entrante = m.direction === 'in'
    return (
        <div
            ref={refEl}
            data-testid={resaltada ? 'mensaje-resaltado' : undefined}
            className={cn(
                entrante ? 'flex justify-start' : 'flex justify-end',
                entra && (entrante ? 'burbuja-entra-in' : 'burbuja-entra-out'),
            )}
            style={entra ? { animationDelay: `${atraso}ms` } : undefined}
        >
            <div
                className={cn(
                    compacto ? 'max-w-[92%] rounded-md px-2 py-1 text-xs' : 'max-w-[85%] rounded-lg px-3 py-1.5 text-sm',
                    'transition-shadow duration-300',
                    entrante ? 'bg-neutral-800 text-neutral-200' : 'bg-sky-950 text-sky-100',
                    resaltada && 'ring-2 ring-amber-300/90 shadow-[0_0_0_6px_rgba(252,211,77,0.18)]',
                )}
            >
                <AdjuntosDelMensaje adjuntos={m.adjuntos} />
                {m.content.trim() !== '' && <p className="whitespace-pre-wrap break-words">{m.content}</p>}
                {m.at && (
                    <span className="mt-0.5 block text-right font-mono text-[10px] text-neutral-500">{hora(m.at)}</span>
                )}
            </div>
        </div>
    )
}
